/**
 * What the browser has actually promised about keeping this data.
 *
 * Deliberately does not try to detect private browsing. Every technique for that
 * is a heuristic that breaks between browser versions, and a warning that fires on
 * the wrong people teaches them to ignore warnings. What *is* knowable is whether
 * the browser has granted persistent storage — and when it has not, the honest
 * message covers the private-window case without having to guess at it.
 */

export interface StorageStatus {
  /** The browser has promised not to evict this data on its own. */
  persisted: boolean;
  /** Bytes in use, where the browser will say. */
  usage: number | null;
  quota: number | null;
  supported: boolean;
}

export async function storageStatus(): Promise<StorageStatus> {
  const api = navigator.storage;
  if (!api?.persisted) return { persisted: false, usage: null, quota: null, supported: false };
  const persisted = await api.persisted().catch(() => false);
  let usage: number | null = null;
  let quota: number | null = null;
  if (api.estimate) {
    const e = await api.estimate().catch(() => null);
    usage = e?.usage ?? null;
    quota = e?.quota ?? null;
  }
  return { persisted, usage, quota, supported: true };
}

/** Asks again. Browsers grant this far more readily once the app is installed. */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  return navigator.storage.persist().catch(() => false);
}

export function formatBytes(n: number | null): string {
  if (n === null) return 'unknown';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} kB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}
